import { supabase } from 'lib/supabase';

// Talks to the gift Edge Functions. Buyers don't sign in, so the shared site client is used.

export type PlanId = '3m' | '6m' | '12m';

export interface GiftPlan {
  plan: PlanId;
  months: number;
  amount: number; // minor units, e.g. 2999
  currency: string; // e.g. "usd"
}

export interface CheckoutRequest {
  plan: PlanId;
  purchaser_name: string;
  purchaser_email: string;
  recipient_name: string;
  recipient_email: string;
  message: string;
  send_on: string; // YYYY-MM-DD, or "" to send now
  timezone: string;
}

export type GiftOrder =
  | { status: 'pending' }
  | { status: 'refunded' | 'disputed' }
  | {
      status: 'ready';
      code: string;
      plan_months: number;
      recipient_name: string | null;
      recipient_email: string | null;
      deliver_at: number; // epoch seconds
      delivered: boolean;
      delivery_timezone: string | null;
      expires_at: number; // epoch seconds
    };

export const formatPrice = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: currency.toLowerCase() === 'usd' ? 'narrowSymbol' : 'symbol',
  }).format(amount / 100) + (currency.toLowerCase() === 'usd' ? ' USD' : '');

export const planName = (months: number) => (months === 12 ? '12 months' : `${months} months`);

export async function fetchPlans(): Promise<GiftPlan[]> {
  const { data, error } = await supabase.functions.invoke('create-gift-checkout', { method: 'GET' });
  if (error || !data?.plans) throw new Error('prices_unavailable');
  return data.plans as GiftPlan[];
}

const CHECKOUT_ERRORS: Record<string, string> = {
  invalid_purchaser_email: 'Please check your email address.',
  invalid_recipient_email: "Please check the recipient's email address.",
  schedule_requires_recipient_email: "To schedule a gift, add the recipient's email address.",
  invalid_send_on: 'Please choose a valid delivery date.',
  send_on_too_far: 'Please choose a delivery date within the next year.',
};

export async function startCheckout(req: CheckoutRequest): Promise<string> {
  const { data, error } = await supabase.functions.invoke('create-gift-checkout', { body: req });
  if (!error && data?.url) return data.url as string;

  let code = '';
  const ctx = (error as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.json === 'function') {
    code = (await ctx.json().catch(() => ({})))?.error ?? '';
  }
  throw new Error(CHECKOUT_ERRORS[code] ?? "We couldn't start checkout just now. Please try again in a moment.");
}

export async function fetchOrder(sessionId: string): Promise<GiftOrder> {
  const { data, error } = await supabase.functions.invoke(
    `get-gift-order?session_id=${encodeURIComponent(sessionId)}`,
    { method: 'GET' },
  );
  if (error || !data?.status) throw new Error('lookup_failed');
  return data as GiftOrder;
}
